import React from 'react';

// WP2.1B-R2: locked Public Portal page-hero composition (giisdecsite_30 · public
// information views). Forest brand wash + dark-green depth, pill badge, Sora
// two-tone headline, and a centered supporting line. Presentation-only; consumes
// frozen EDS v1.0 tokens exclusively.

interface Props {
  /** Small pill eyebrow, e.g. "Service Categories". */
  badge: string;
  /** Headline node (supports two-/three-tone emphasis via <span>). */
  title: React.ReactNode;
  /** Supporting sentence under the headline. */
  subtitle?: string;
  /** Center the composition (default) or left-align. */
  align?: 'center' | 'left';
}

const PublicHero: React.FC<Props> = ({ badge, title, subtitle, align = 'center' }) => {
  const centered = align === 'center';
  return (
    <section className="relative overflow-hidden bg-eds-forest text-white font-eds-body">
      {/* Layered brand depth — governed, gradient-only (no external asset). */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-eds-forest via-eds-forest to-eds-forest-2"
        aria-hidden="true"
      />
      <div
        className="absolute -top-24 -right-24 w-96 h-96 rounded-eds-pill bg-eds-emerald/15 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-32 -left-16 w-96 h-96 rounded-eds-pill bg-eds-emerald-2/10 blur-3xl"
        aria-hidden="true"
      />

      <div
        className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 ${
          centered ? 'text-center flex flex-col items-center' : ''
        }`}
      >
        <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-eds-pill text-xs font-semibold bg-white/10 border border-white/15 text-eds-mint">
          <span className="w-1.5 h-1.5 rounded-eds-pill bg-eds-emerald" aria-hidden="true" />
          {badge}
        </span>

        <h1 className="font-eds-display text-4xl md:text-5xl lg:text-[3.4rem] font-bold tracking-[-0.02em] mt-6 leading-[1.05] max-w-4xl">
          {title}
        </h1>

        {subtitle && (
          <p className={`font-eds-body text-white/70 mt-5 text-lg leading-relaxed max-w-2xl ${centered ? 'mx-auto' : ''}`}>
            {subtitle}
          </p>
        )}
      </div>
    </section>
  );
};

export default PublicHero;
